import {
  EnvironmentProviders,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { Settings } from 'luxon';
import { environment } from '@eagle/env';
import { HttpLoaderFactory } from './i18n.lib';

export enum AppLocaleEnum {
  NL = 'nl',
  EN = 'en',
}
const APP_LOCALE_KEY = 'eagleLocale';

export function provideI18n(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: LOCALE_ID, useValue: AppLocaleEnum.NL },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideEnvironmentInitializer(() => initI18n(inject(TranslateService))),
  ]);
}

function initI18n(translateService: TranslateService): void {
  registerLocaleData(localeNl, AppLocaleEnum.NL);
  translateService.langs.push(...Object.values(AppLocaleEnum));

  const environmentLocale = environment.tag === 'prod' ? AppLocaleEnum.NL : AppLocaleEnum.EN;
  const savedLocale = localStorage.getItem(APP_LOCALE_KEY);
  const locale =
    savedLocale && translateService.getLangs().includes(savedLocale)
      ? savedLocale
      : environmentLocale;

  translateService.setDefaultLang(environmentLocale);
  translateService.use(locale);

  translateService.onLangChange.subscribe(({ lang }) => {
    document.documentElement.lang = lang;
    if (lang === unsavedLocale) {
      unsavedLocale = null;
    } else {
      localStorage.setItem(APP_LOCALE_KEY, lang);
    }
    Settings.defaultLocale = lang;
  });
  Settings.defaultLocale = locale;
}

let unsavedLocale: string | null = null;

/** Shows the app in `locale` without storing it as the user's language (e.g. pages for external signers). */
export function useLanguageWithoutSaving(
  translateService: TranslateService,
  locale: AppLocaleEnum
): void {
  if (translateService.currentLang !== locale) {
    unsavedLocale = locale;
  }
  translateService.use(locale);
}
